import debounce from 'lodash/debounce';
import { isMobileViewSize } from '../../tools';

/**
 * Adds functionality to filter accordions
 */
class HedinAccordion {

  /**
   * Accordion element
  */
  #element;

  /**
   * Element for the content
  */
  #contentContainer;

  /**
   * Element for interacting with the accordion
  */
  #handle;

  /**
   * Element for input labels when accordion element is closed
  */
  #details;

  /**
   * Title text
   */
  #title;

  /**
   * Constructor. Starts everything up if accordion elements are found
   * @param {HTMLElement} element element to bind accordion item functionality
   */
  constructor(element) {
    this.#element = element;
    this.#contentContainer = this.#element.querySelector('.hedin-accordion__content-container');
    this.#handle = this.#element.querySelector('.hedin-accordion__handle');
    this.#details = this.#element.querySelector('.hedin-accordion__handle-details');
    this.#title = this.#element.querySelector('.hedin-accordion__handle-title');

    this.#bindElements();

    window.addEventListener('load', () => {
      if (this.#details) {
        this.#getInputTexts(this.#element);
      }

      if (this.#element.classList.contains('open')) {
        this.#contentContainer.style.overflow = 'unset';
        this.#contentContainer.style.maxHeight = 'none';
        this.#contentContainer.style.display = 'block';
      }
    });

    element.accordion = this;

    this._onResizeDebounced = debounce(this.#onResize, 1000, { trailing: true, leading: false });
    window.addEventListener('resize', this._onResizeDebounced.bind(this));
  }

  /**
   * Show/Hide accrodion item
   * @param {boolean} show if value is true accordion is opened otherwise it's closed
   */
  set open(show) {
    this.#toggleAccordion(show)
  }

  /**
   * Set accordion title
   * @param {string} text text
   */
  set text(text) {
    this.#title.innerText = text;
  }

  /**
   * Is accordion visible only on mobile view sizes
   * @return {boolean} true if only use in mobile sizes
   */
  isOnlyMobile() {
    return this.#element.classList.contains('hedin-accordion--mobile-only');
  }

  /**
   * Bind elements
   */
  #bindElements() {
    this.#handle.addEventListener('click', (event) => {
      if(this.isOnlyMobile() && !isMobileViewSize()) {
        return;
      }

      event.preventDefault();
      const isOpen = !this.#element.classList.contains('open');
      this.open = isOpen;
      this.#element.dispatchEvent( new CustomEvent('hedin-accordion-toggle', { detail: isOpen }) );
    });

    // If this element is used in car search
    // there is a custom event when tag-list changes
    // If that fires, update input texts
    document.addEventListener('tagListChange', () => {
      this.#getInputTexts(this.#element);
    });

    /**
     * This custom element is fired after a search parameters and checkbox, select are emptied in car-search.js.
     */
    document.addEventListener('searchParametersManipulated', () => {
      this.#getInputTexts(this.#element);
    })
  }

  /**
   * Show/Hide accrodion item
   * @param {boolean} show if value is true accordion is opened otherwise it's closed
   */
  #toggleAccordion( show = !this.#element.classList.contains('open')) {
    let isDisabled = this.#element.classList.contains('disabled');

    if (!isDisabled) {
      let isOpen = this.#element.classList.toggle('open', show);

      if (isOpen) {
        // Hide sibling accordion elements
        const siblingAccordions = Array.from(this.#element.parentNode.querySelectorAll('.hedin-accordion:not(.independent).open')).filter((element) => !element.isSameNode(this.#element));
        siblingAccordions.forEach((element) => {
          element.classList.remove('open');
          this.#getInputTexts(element);
        });
      } else {
        this.#getInputTexts(this.#element);
      }
    }
  }

  /**
   * Function is called on page is resized.
   *
   * If the accordion is closed in mobile and after that user tilts device from portrait to landscape mode.
   * Accordion buttons are not anymore visible but view is still closed. This automaticly in this case
   * opens the accordion on desktop view.
   */
  #onResize() {
    if( this.isOnlyMobile() && !isMobileViewSize() ) {
      this.#toggleAccordion(true);
    }
  }

  /**
   * Set input labels to handle details element
   * @param {HTMLElement} element element to get input labels from
   */
  #getInputTexts(element) {

    const detailsElement = element.querySelector('.hedin-accordion__handle-details');

    if (detailsElement) {

      const sourceElementsSelector = element.dataset.inputLabelSource;
      const sourceElementsSelectorMin = element.dataset.inputLabelSourceMin;
      const sourceElementsSelectorMax = element.dataset.inputLabelSourceMax;

      const elementSelectors = [sourceElementsSelector, sourceElementsSelectorMin, sourceElementsSelectorMax].filter(Boolean).join(', ');

      const sourceElements = elementSelectors ? element.querySelectorAll(elementSelectors) : null;

      const sourceElementsMin = sourceElementsSelectorMin ? element.querySelectorAll(sourceElementsSelectorMin) : null;
      const sourceElementsMax = sourceElementsSelectorMax ? element.querySelectorAll(sourceElementsSelectorMax) : null;

      const labelTexts = [];

      if (sourceElements?.length) {
        sourceElements.forEach((sourceElement) => {
          labelTexts.push(sourceElement.innerText.replace(/,/g, '').trim());
        });
      }

      if (labelTexts.length === 1 && sourceElementsMin?.length) {
        detailsElement.dataset.prefix = 'min. ';
      } else if (labelTexts.length === 1 && sourceElementsMax?.length) {
        detailsElement.dataset.prefix = 'max. ';
      } else {
        detailsElement.dataset.prefix = '';
      }

      const labelSeparator = element.dataset.inputLabelSourceSeparator || ', ';

      if( elementSelectors.length ) {
        if (labelTexts.length) {
          detailsElement.innerHTML = labelTexts.join(labelSeparator);
        } else {
          detailsElement.innerHTML = '';
        }
      }
    }
  }

  /**
   * init Static initializer of the class.
   */
  static init() {
    const hedinAccordions = document.querySelectorAll('.hedin-accordion');

    hedinAccordions.forEach((element) => {
      new HedinAccordion(element);
    });
  }
}

export default HedinAccordion;
